/**
 * UpgradeScreen — UC-07: Mua nâng cấp.
 *
 * Phase 1: chỉ 1 upgrade type `shop_sign` (Bảng hiệu) với 2 level.
 * Backend endpoints:
 *   - GET /v1/shop/upgrades
 *   - POST /v1/shop/upgrades/{type}/purchase
 *
 * Layout: 2D, không dùng Three.js. Chia sẻ BottomNav với MainCafeScene 3D.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { api, extractErrorMessage } from "../lib/api";
import { track } from "../lib/analytics";
import { newActionId } from "../lib/idempotency";
import { BottomNav } from "../scenes/MainCafe/components/BottomNav";
import { useCafe3dStore } from "../stores/cafe3dStore";

interface UpgradeInfo {
  code: string;
  name_key: string;
  description_key: string | null;
  current_level: number;
  max_level: number;
  next_level: {
    level: number;
    cost_bean: number;
    min_reputation: number;
    description_key: string | null;
  } | null;
}

const UPGRADE_ICON: Record<string, string> = {
  shop_sign: "🪧",
  seats: "🪑",
  machine: "☕",
};

export function UpgradeScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useTelegramBackButton("/");
  const beanCoin = useCafe3dStore((s) => s.beanCoin);
  const reputation = useCafe3dStore((s) => s.reputation);
  const shopName = useCafe3dStore((s) => s.shopName);
  const initFromBackend = useCafe3dStore((s) => s.initFromBackend);

  const [upgrades, setUpgrades] = useState<UpgradeInfo[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // upgrade code đang mua
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Init shop nếu chưa.
        if (!shopName) {
          await initFromBackend();
        }
        const res = await api.get<{ data: { upgrades: UpgradeInfo[] } }>(
          "/shop/upgrades",
        );
        if (!cancelled) setUpgrades(res.data.data.upgrades);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [initFromBackend, shopName]);

  async function purchase(code: string) {
    setBusy(code);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post<{
        data: {
          shop_state: { bean_coin: number; upgrades: Record<string, number> };
          purchased: { code: string; level: number; cost: number };
          milestones_unlocked: Array<{
            code: string;
            rewards: Record<string, number>;
          }>;
        };
      }>(`/shop/upgrades/${code}/purchase`, {
        client_action_id: newActionId(),
      });

      // Refresh state.
      await initFromBackend();
      // Refresh upgrade list.
      const list = await api.get<{ data: { upgrades: UpgradeInfo[] } }>(
        "/shop/upgrades",
      );
      setUpgrades(list.data.data.upgrades);

      setSuccess(
        `🎉 ${t(`upgrade.type.${code}`)} → ${t("upgrade.level", { current: res.data.data.purchased.level, max: res.data.data.purchased.level })}`,
      );

      // Track milestone first_upgrade.
      const ms = res.data.data.milestones_unlocked.find(
        (m) => m.code === "first_upgrade",
      );
      if (ms) {
        track("first_upgrade_purchased", { upgrade_code: code });
        // Push toast queue để MilestoneToast hiển thị.
        for (const m of res.data.data.milestones_unlocked) {
          useCafe3dStore.getState().pushMilestone({
            code: m.code,
            rewardBean: m.rewards.bean ?? 0,
          });
        }
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="w-full h-full max-w-[480px] mx-auto flex flex-col bg-cream-50 relative overflow-hidden safe-area-top">
      {/* Header */}
      <div className="px-4 py-3 bg-coffee-900 text-cream-50 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="text-sm opacity-80 hover:opacity-100"
        >
          {t("gameplay.back_to_shop")}
        </button>
        <h1 className="text-base font-bold font-baloo">{t("upgrade.screen_title")}</h1>
        <div className="text-sm font-baloo">
          ☕ {beanCoin.toLocaleString()}
        </div>
      </div>

      {/* Resources */}
      <div className="px-4 py-2 bg-cream-100 text-xs text-coffee-700 flex gap-4">
        <span>{t("hud.bean")}: <b className="text-coffee-900">{beanCoin}</b></span>
        <span>{t("hud.reputation")}: <b className="text-coffee-900">{reputation}</b></span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {error && (
          <div className="bg-red-stool/10 border border-red-stool/30 text-red-stool rounded-lg px-3 py-2 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-leaf/10 border border-green-leaf/30 text-green-leaf rounded-lg px-3 py-2 text-sm">
            {success}
          </div>
        )}

        {upgrades === null ? (
          <div className="text-center text-coffee-700 py-8">
            {t("upgrade.loading")}
          </div>
        ) : upgrades.length === 0 ? (
          <div className="text-center text-coffee-700 py-8">
            {t("upgrade.empty")}
          </div>
        ) : (
          upgrades.map((u) => {
            const isMaxed = u.next_level === null;
            const canAfford =
              !isMaxed &&
              beanCoin >= u.next_level!.cost_bean &&
              reputation >= u.next_level!.min_reputation;
            const isBusy = busy === u.code;

            return (
              <div
                key={u.code}
                className="bg-white rounded-2xl p-4 shadow-soft-amber border border-coffee-700/10"
              >
                <div className="flex items-start gap-3">
                  <div className="w-16 h-16 rounded-xl bg-cream flex items-center justify-center text-3xl">
                    {UPGRADE_ICON[u.code] ?? "🔧"}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-coffee-900">
                        {t(`upgrade.type.${u.code}`, u.code)}
                      </h3>
                      <span className="text-xs bg-coffee-700/10 text-coffee-700 px-2 py-0.5 rounded-full">
                        {t("upgrade.level", { current: u.current_level, max: u.max_level })}
                      </span>
                    </div>
                    <p className="text-xs text-coffee-700 mt-1">
                      {t(`upgrade.desc.${u.code}`, "")}
                    </p>
                  </div>
                </div>

                {isMaxed ? (
                  <div className="mt-3 text-center text-sm text-coffee-700/60">
                    {t("upgrade.maxed")}
                  </div>
                ) : (
                  <>
                    {t(`upgrade.effect.${u.code}_${u.next_level!.level}`, "") && (
                      <div className="mt-3 text-xs text-green-leaf font-medium px-1">
                        {t(`upgrade.effect.${u.code}_${u.next_level!.level}`, "")}
                      </div>
                    )}
                    <div className="mt-2 bg-cream/60 rounded-lg p-2 text-xs text-coffee-700 space-y-1">
                      <div className="flex justify-between">
                        <span>{t("upgrade.next_level_required", { level: u.next_level!.level })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>☕ {t("hud.bean")}</span>
                        <span
                          className={
                            beanCoin >= u.next_level!.cost_bean
                              ? "text-green-leaf font-bold"
                              : "text-red-stool font-bold"
                          }
                        >
                          {u.next_level!.cost_bean.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>⭐ {t("hud.reputation")}</span>
                        <span
                          className={
                            reputation >= u.next_level!.min_reputation
                              ? "text-green-leaf font-bold"
                              : "text-red-stool font-bold"
                          }
                        >
                          {u.next_level!.min_reputation}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => purchase(u.code)}
                      disabled={!canAfford || isBusy}
                      className="mt-3 w-full py-2.5 rounded-xl bg-red-stool text-cream-50 font-bold disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_0_#8E2926] active:translate-y-[2px] active:shadow-[0_2px_0_#8E2926] transition"
                    >
                      {isBusy
                        ? t("upgrade.buying")
                        : !canAfford
                          ? t("upgrade.not_enough")
                          : t("upgrade.buy_button", { cost: u.next_level!.cost_bean })}
                    </button>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      <BottomNav />
    </div>
  );
}
